<template lang="pug">
.consultation-note-fullview
  template(v-if="isFetching")
    v-container.pa-2(fluid)
      v-row(no-gutters)
        v-col
          v-row
            v-col(cols="1")
              v-btn(icon :loading="isLeaving" @click="onClickBack")
                v-icon(color="primary") mdi-arrow-left
            v-col
              v-skeleton-loader(type="article@5")
        v-col
          v-skeleton-loader(type="article@5")
  template(v-else)
    v-row.px-8.pt-4
      v-col(:cols="showRightPanel ? 8 : 12")
        .d-flex.justify-space-between
          v-btn(icon :loading="isLeaving" @click="onClickBack")
            v-icon(color="primary") mdi-arrow-left
          v-btn.ml-1(v-if="!showRightPanel" @click="toggleRightPanel" icon small color="primary")
            v-icon(size=22) $yzi-layout-left
        v-card.round-xl(outlined)
          .d-flex.flex-column
            .neutral300.px-4.py-2
              v-tooltip(top color="black" content-class="black tooltip-top-arrow" v-if="consultation.case")
                template(#activator="{ on, attrs }")
                  div(v-bind="attrs" v-on="on")
                    .text-sm.font-semibold.ellipsis-text {{ $t('visit_note') }}: 
                      span.font-normal.cursor-help.neutral700--text {{ consultation.case }}
                .font-normal.cursor-help {{ consultation.case }}
              .text-sm.font-semibold(v-else) {{ $t('visit_note') }}: -
            v-divider
            div(v-if="isDoctor")
              text-editor(ref="froala" :parentForm.sync="consultation.note" :height="textEditorHeight" :isLoading="isUpdatingNote" @onSave="updateNote(true)")
            div(v-else)
              v-sheet.pa-5.overflow-y-auto(:height="textEditorHeight" v-html="consultation.note")
      v-divider(vertical v-if="showRightPanel")
      v-col.pa-0(cols=4 v-show="showRightPanel")
        patient-history(:parentPatient="patient" :parentRegistration="registration" :parentActiveMedications="activeMedications" :showRightPanel.sync="showRightPanel" :height="patientHistoryPanelHeight" :hide-view-all="hideViewAll" @updated="onPatientHistoryUpdated" @updateActiveMedications="fetchActiveMedication")

  .dialogs
    consultation-not-found-error-dialog(v-if="showErrorDialog" :open.sync="showErrorDialog")
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import PatientHistory from '@/components/clinic/consultation/patientHistories/PatientHistory.vue'
import TextEditor from '@/components/clinic/consultation/editor/TextEditorFullview.vue'
import ConsultationNotFoundErrorDialog from '@/components/clinic/dialogs/ConsultationNotFoundErrorDialog.vue'

export default {
  components: {
    PatientHistory, TextEditor, ConsultationNotFoundErrorDialog
  },
  data:() => ({
    isFetching: true,
    showRightPanel: true,
    consultation: null,
    isUpdatingNote: false,
    navbarHeight: 72,
    footerHeight: 80,
    showErrorDialog: false,
    isError: false,
    isLeaving: false,
    activeMedications: {
      isFetching: false,
      isError: false,
      data: []
    }
  }),
  async mounted() {
    try {
      this.isFetching = true
      let allFetching = [this.fetchConsultation(true)]
      await Promise.all([...allFetching])
      this.isFetching = false
    } catch (error) {
      this.isError = true
      const { config, status } = error.response
      const consultationEndpoint = config.url.includes('/consultations')
      if (consultationEndpoint && status === 404) {
        this.showErrorDialog = true
        return
      }
      this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
    }
  },
  async beforeRouteLeave(to, from, next) {
    if (!this.isError) {
      this.isLeaving = true
      await this.updateNote(false)
    }
    next()
  },
  computed: {
    ...mapState('user', ['member']),
    ...mapGetters({
      company: 'getCompany',
      isDoctor: 'user/isDoctor'
    }),
    leadPid() {
      return this.$route.params.pid
    },
    patient() {
      return this.consultation.patient
    },
    registration() {
      return this.consultation.registration
    },
    screenHeight () {
      return screen.height
    },
    patientHistoryPanelHeight () {
      return `${this.screenHeight * 45/100}px`
    },
    textEditorHeight () {
      return `${this.screenHeight * 60/100}px`
    },
    hideViewAll () {
      if (this.$route.query?.origin === 'patient-history') {
        return true
      }
      return false
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchConsultation (getActiveMedication = false) {
      return this.$axios.get(`/clinics/${this.company.slug}/consultations/${this.leadPid}`).then(({ data }) => {
        this.consultation = data
        if (getActiveMedication) {
          this.fetchActiveMedication()
        }
      })
    },
    toggleRightPanel () {
      this.showRightPanel = !this.showRightPanel
    },
    onClickBack () {
      if (this.$route.query?.origin === 'patient-profile') {
        const patientId = this.$route.query?.patientId
        const leadPid = this.$route.query?.leadPid
        return this.$router.push(`/admin/clinic/patient/${patientId}?tab=visits&leadPid=${leadPid}`)
      }
      if (this.$route.query?.origin === 'patient-history') {
        const leadPid = this.$route.query?.leadPid
        const page = this.$route.query?.page
        return this.$router.push(`/admin/clinic/consultation/${leadPid}?viewAllHistory=true&page=${page}`)
      }
      return this.$router.push({ name: 'ClinicConsultationDetails', params: { pid: this.leadPid, prevRoute: this.$attrs.prevRoute }})
    },
    async onPatientHistoryUpdated() {
      try {
        await this.updateNote()
        await this.fetchConsultation()
        this.treatmentTableKey++
        this.showMessage(['success', this.$t('success_message.medication_added_successfully')])
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    },
    updateNote(showMessage=false) {
      const payload = {
        note: this.consultation.note
      }
      this.isUpdatingNote = true
      return this.$axios.patch(`/clinics/${this.company.slug}/consultations/${this.leadPid}`, payload)
        .then(({ data }) => {
          this.consultation = data
          if (showMessage) {
            this.showMessage(['success', this.$t('success_message.update_successful')])
          }
        })
        .catch(() => {
          if (showMessage) {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          }
        })
        .finally(() => {
          this.isUpdatingNote = false
        })
    },
    async fetchActiveMedication () {
      this.activeMedications.isFetching = true
      const params = {
        page_size: -1,
        patient: this.patient.id,
        expand: 'medication'
      }
      return this.$axios.get(`/clinics/${this.company.slug}/active_medications`, { params })
        .then(({ data }) => {
          if (data?.results?.length > 0) {
            this.activeMedications.data = data.results
          } else {
            this.activeMedications.data = []
          }
        })
        .catch(() => {
          this.activeMedications.isError = true
        })
        .finally(() => {
          this.activeMedications.isFetching = false
        })
      }
  },
}
</script>

<style lang="scss" scoped>
// .consultation-note-fullview {
//   position: absolute;
  // top: 0;
  // width: 100% !important;
  // height: 100% !important;
// }
// .middle-content {
//   padding-bottom: 340px;
// }
.h-100 {
  height: 100% !important;
}
.write-panel {
  height: 100% !important;
  padding: 16px 36px 0px 36px;
}
.overflow-hidden {
  overflow: hidden !important;
}
.overflow {
  overflow: scroll;
}
.btn-normal {
  border: 1px solid #EEEEEE !important;
}
.btn-danger {
  border: none !important;
  background: #FFEBEB !important;
}
.number-padding {
  padding-left: 10px !important;
  padding-right: 10px !important;
}
.ellipsis-text {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
}
.profile-height {
  height: 130px;
}
</style>